import {
  normalizeMercadoPagoBasisTheoryPayment,
  type MercadoPagoBasisTheoryPayment,
} from 'services/mercadoPagoBasisTheoryPayment';
import {
  normalizeMercadoPagoBasisTheoryChallenge,
  type MercadoPagoBasisTheoryChallengeData,
} from './challenge';

interface Attempt {
  token: string;
  payment: MercadoPagoBasisTheoryPayment;
  challenge?: MercadoPagoBasisTheoryChallengeData;
  invalidChallenge: boolean;
}

interface Ownership {
  expiresAt: number;
  challengeCompleted: boolean;
  reconcileAfter?: number;
}

const MAX_AGE_MS = 30 * 60 * 1000;
let transientAttempt: Attempt | undefined;
let transientExpiry: ReturnType<typeof setTimeout> | undefined;
const ownershipFallback = new Map<string, Ownership>();

function ownershipKey(token: string, id: string) {
  return `mercadopago_bt_attempt:${encodeURIComponent(token)}:${encodeURIComponent(id)}`;
}

function writeOwnership(token: string, id: string, ownership: Ownership) {
  const key = ownershipKey(token, id);
  const now = Date.now();
  ownershipFallback.forEach((entry, entryKey) => {
    if (entry.expiresAt <= now) ownershipFallback.delete(entryKey);
  });
  ownershipFallback.set(key, ownership);
  try {
    sessionStorage.setItem(key, JSON.stringify(ownership));
  } catch {
    // Privacy settings can disable storage; the current attempt still works in memory.
  }
}

export function readMercadoPagoBasisTheoryOwnership(
  token: string,
  id: string,
): Ownership | undefined {
  const key = ownershipKey(token, id);
  let ownership = ownershipFallback.get(key);
  try {
    const raw = sessionStorage.getItem(key);
    if (raw) {
      const stored: unknown = JSON.parse(raw);
      if (
        typeof stored === 'object' &&
        stored !== null &&
        'expiresAt' in stored &&
        typeof stored.expiresAt === 'number' &&
        'challengeCompleted' in stored &&
        typeof stored.challengeCompleted === 'boolean'
      ) {
        ownership = { expiresAt: stored.expiresAt, challengeCompleted: stored.challengeCompleted };
        if (
          'reconcileAfter' in stored &&
          typeof stored.reconcileAfter === 'number' &&
          Number.isFinite(stored.reconcileAfter)
        ) {
          ownership.reconcileAfter = stored.reconcileAfter;
        }
      }
    }
  } catch {
    // Use the safe in-memory routing marker if storage is unavailable or corrupted.
  }
  if (ownership && ownership.expiresAt > Date.now()) return ownership;
  clearMercadoPagoBasisTheoryAttempt(token, id, true);
  return undefined;
}

export function captureMercadoPagoBasisTheoryAttempt(token: string, response: unknown): Attempt {
  const payment = normalizeMercadoPagoBasisTheoryPayment(response);
  if (!payment) throw new Error('mercadopago_bt_invalid_payment');
  const payload =
    typeof response === 'object' && response !== null && 'payload' in response
      ? response.payload
      : response;
  let rawChallenge: unknown;
  if (typeof payload === 'object' && payload !== null) {
    if ('threeDsChallenge' in payload) rawChallenge = payload.threeDsChallenge;
    else if ('three_ds_challenge' in payload) rawChallenge = payload.three_ds_challenge;
  }
  const challenge = normalizeMercadoPagoBasisTheoryChallenge(rawChallenge);
  const attempt = {
    token,
    payment,
    challenge,
    invalidChallenge: payment.status === 'pending' && payment.using3dSecure && !challenge,
  };
  const ownership = readMercadoPagoBasisTheoryOwnership(token, payment.id);
  writeOwnership(
    token,
    payment.id,
    ownership ?? {
      expiresAt: Date.now() + MAX_AGE_MS,
      challengeCompleted: false,
    },
  );
  transientAttempt = attempt;
  clearTimeout(transientExpiry);
  transientExpiry = setTimeout(
    () => {
      transientAttempt = undefined;
      transientExpiry = undefined;
    },
    5 * 60 * 1000,
  );
  return attempt;
}

export function takeMercadoPagoBasisTheoryAttempt(token: string, id: string): Attempt | undefined {
  if (transientAttempt?.token !== token || transientAttempt.payment.id !== id) return undefined;
  const attempt = transientAttempt;
  transientAttempt = undefined;
  clearTimeout(transientExpiry);
  transientExpiry = undefined;
  return attempt;
}

export function completeMercadoPagoBasisTheoryChallenge(token: string, id: string) {
  const ownership = readMercadoPagoBasisTheoryOwnership(token, id);
  if (ownership)
    writeOwnership(token, id, {
      ...ownership,
      challengeCompleted: true,
      reconcileAfter: Date.now() + 4000,
    });
  clearMercadoPagoBasisTheoryAttempt(token, id);
}

export function clearMercadoPagoBasisTheoryAttempt(token: string, id: string, terminal = false) {
  if (transientAttempt?.token === token && transientAttempt.payment.id === id) {
    transientAttempt = undefined;
    clearTimeout(transientExpiry);
    transientExpiry = undefined;
  }
  if (!terminal) return;
  const key = ownershipKey(token, id);
  ownershipFallback.delete(key);
  try {
    sessionStorage.removeItem(key);
  } catch {
    // Nothing sensitive is stored, and an expired marker is ignored on its next read.
  }
}

/** Only the public snapshot and its routing mode may cross the Redux receipt boundary. */
export function mercadoPagoBasisTheoryReceipt(payment: MercadoPagoBasisTheoryPayment) {
  const numericId = Number(payment.id);
  return {
    ...payment,
    id: Number.isSafeInteger(numericId) && numericId > 0 ? numericId : payment.id,
    cardCheckoutMode: 'basis_theory',
  };
}
